import React, { createContext, useState, useEffect, useRef } from 'react';
import API from '../api/axios';
import { identifyOpenReplayUser } from '../utils/openreplay';

export const AuthContext = createContext();

// Keys earlier builds kept in localStorage. Tokens now live only in HttpOnly
// cookies and the user snapshot only in memory; purge any leftovers.
const LEGACY_STORAGE_KEYS = [
  'token', 'user', 'tokenExpiry',
  'impersonator_token', 'impersonator_user', 'impersonator_tokenExpiry',
];

function purgeLegacyStorage() {
  try {
    LEGACY_STORAGE_KEYS.forEach((k) => localStorage.removeItem(k));
  } catch (e) { /* storage unavailable — nothing to purge */ }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isImpersonating, setIsImpersonating] = useState(false);
  const logoutTimer = useRef(null);

  // Absolute session cap: log out when the server-issued token expires. An
  // expiring impersonation hands back to the superadmin's session instead.
  const scheduleLogout = (expiryMs, onExpire = () => logout()) => {
    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
    if (!expiryMs) return;

    const ms = expiryMs - Date.now();
    if (ms > 0) {
      logoutTimer.current = setTimeout(onExpire, ms);
    } else {
      // Already expired
      onExpire();
    }
  };

  // Rebuild the in-memory session from the HttpOnly cookie.
  const restoreSession = async () => {
    try {
      const { data } = await API.get('/auth/session');
      if (data.success) {
        setUser(data.user);
        setIsImpersonating(!!data.impersonating);
        scheduleLogout(data.expiresAt, data.impersonating ? () => stopImpersonation() : undefined);
        return true;
      }
    } catch (e) { /* no valid session cookie */ }
    setUser(null);
    setIsImpersonating(false);
    return false;
  };

  useEffect(() => {
    purgeLegacyStorage();
    (async () => {
      // If an impersonation expired while the tab was closed, unwind it back to
      // the parked superadmin session (the server answers 400 when there is none).
      if (!(await restoreSession())) {
        try {
          const { data } = await API.post('/impersonation/stop');
          if (data?.restored) await restoreSession();
        } catch (e) { /* not impersonating — stay logged out */ }
      }
      setLoading(false);
    })();

    return () => {
      if (logoutTimer.current) clearTimeout(logoutTimer.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const login = async (email, password, twoFactorCode = '') => {
    const { data } = await API.post('/auth/login', { email, password, twoFactorCode });

    if (data.success && data.user) {
      setUser(data.user);
      setIsImpersonating(false);
      scheduleLogout(data.expiresAt);
    }

    return data;
  };

  const register = async (formData) => {
    const { data } = await API.post('/auth/register', formData);
    if (data.expiresAt) {
      setUser(data.user);
      scheduleLogout(data.expiresAt);
    }
    return data;
  };

  // Clears the first-time onboarding banner: persists the dismissal server-side
  // (so it never shows again) and updates the local user snapshot immediately.
  const dismissOnboardingNotice = async () => {
    setUser(prev => (prev ? { ...prev, showFirstTimeNotice: false } : prev));
    try {
      await API.post('/auth/dismiss-onboarding-notice');
    } catch (e) {
      // Best-effort: the banner is already gone locally; the flag will also be
      // cleared on the next logout, so a transient failure is non-blocking.
    }
  };

  // Merge partial fields into the current user so the change (e.g. a freshly
  // uploaded avatar_url) is reflected everywhere that reads from context —
  // including the navbar — without requiring a re-login.
  const updateUser = (partial) => {
    setUser(prev => (prev ? { ...prev, ...partial } : prev));
  };

  // Switch the active role (for users with multiple roles). The server swaps in
  // the role-scoped session cookie; we update the active role while preserving
  // the rest of the user snapshot (avatar, name, etc.).
  const switchRole = async (role) => {
    const { data } = await API.post('/auth/switch-role', { role });
    if (data.success) {
      setUser(prev => ({ ...(prev || {}), role: data.role, roles: data.roles }));
      scheduleLogout(data.expiresAt, isImpersonating ? () => stopImpersonation() : undefined);
    }
    return data;
  };

  const logout = () => {
    // Logging out also satisfies the "first-time only" contract — clear the
    // notice server-side (best-effort, while the session is still present),
    // then have the server clear the session + any impersonator cookie.
    const dismiss = user?.showFirstTimeNotice
      ? API.post('/auth/dismiss-onboarding-notice').catch(() => {})
      : Promise.resolve();
    dismiss.finally(() => API.post('/auth/logout').catch(() => {}));

    if (logoutTimer.current) {
      clearTimeout(logoutTimer.current);
      logoutTimer.current = null;
    }
    setIsImpersonating(false);
    setUser(null);
  };

  // Keep the session replay linked to whoever is logged in (by user_id only).
  useEffect(() => {
    if (loading) return;
    identifyOpenReplayUser(user, { impersonating: isImpersonating });
  }, [loading, user?.user_id, user?.role, isImpersonating]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-logout after 30 minutes of INACTIVITY. The absolute 8-hour session cap
  // is enforced separately by the JWT expiry via scheduleLogout. On logout the
  // user state clears and the router sends them to the public homepage.
  useEffect(() => {
    if (!user) return;
    const IDLE_LIMIT_MS = 30 * 60 * 1000;
    let idleTimer = setTimeout(logout, IDLE_LIMIT_MS);
    let lastReset = Date.now();
    const reset = () => {
      const now = Date.now();
      if (now - lastReset < 1000) return; // throttle resets to once/second
      lastReset = now;
      clearTimeout(idleTimer);
      idleTimer = setTimeout(logout, IDLE_LIMIT_MS);
    };
    const events = ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart', 'click'];
    events.forEach((e) => window.addEventListener(e, reset, { passive: true }));
    return () => {
      clearTimeout(idleTimer);
      events.forEach((e) => window.removeEventListener(e, reset));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Begin impersonating `targetId`. The server parks the superadmin's own
  // session in a separate HttpOnly cookie and swaps in the impersonation one;
  // no token ever reaches client storage.
  const startImpersonation = async (targetId) => {
    const { data } = await API.post(`/impersonation/start/${targetId}`);
    if (!data?.success) {
      throw new Error(data?.message || 'Impersonation failed');
    }

    await restoreSession();
    return data;
  };

  const stopImpersonation = async () => {
    let restored = false;
    try {
      const { data } = await API.post('/impersonation/stop');
      restored = !!data?.restored;
    } catch { /* fall through to a clean login */ }

    if (!restored || !(await restoreSession())) {
      // Original session was lost — force a clean login.
      logout();
    }
  };

  return (
    <AuthContext.Provider value={{
      user, login, register, logout, loading,
      startImpersonation, stopImpersonation, isImpersonating,
      dismissOnboardingNotice, updateUser, switchRole
    }}>
      {children}
    </AuthContext.Provider>
  );
};
